import { createContext, useContext, useEffect, useState } from "react";
import { supabase } from "../lib/supabase";

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [session, setSession] = useState(undefined);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  async function fetchProfile(userId) {
    // Retry up to 3 times — RLS can fail on first login
    // if auth_id was just linked
    for (let attempt = 0; attempt < 3; attempt++) {
      const { data } = await supabase
        .from("employees")
        .select("*")
        .eq("auth_id", userId)
        .maybeSingle();

      if (data) {
        setProfile(data);
        setLoading(false);
        return;
      }

      // Wait before retrying
      if (attempt < 2) await new Promise((r) => setTimeout(r, 800));
    }

    // After all retries, no employee record found
    setProfile(null);
    setLoading(false);
  }

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setSession(session);
      if (session) fetchProfile(session.user.id);
      else setLoading(false);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (_event, session) => {
        setSession(session);
        if (session) {
          await fetchProfile(session.user.id);
        } else {
          setProfile(null);
          setLoading(false);
        }
      }
    );
    return () => subscription.unsubscribe();
  }, []); // eslint-disable-line

  const signIn  = async (email, password) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error;
  };

  const signOut = () => supabase.auth.signOut();

  return (
    <AuthContext.Provider value={{
      session,
      profile,
      loading,
      isAdmin: profile?.role === "admin",
      isStaff: profile?.role === "staff",
      signIn,
      signOut,
    }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);